import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { Role } from '../models/role';
import { Observable } from 'rxjs';
import { User } from '../models/user';
import { PaginationEnvelope } from '../models/dto/paginationEnvelope';
import { Audit } from '../models/audit';
import { PermissionGroup } from '../models/permission-group';
import { AvailablePermission } from '../models/dto/availablePermission';
import {
  EndpointPermission,
  EndpointPermissionSyncResult,
  EndpointPermissionUpdate,
} from '../models/endpoint-permission';
import { RemovedEndpoint } from '../models/removed-endpoint';

@Injectable({
  providedIn: 'root',
})
export class AdminService {
  baseUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  getUsersWithRoles() {
    return this.http.get(this.baseUrl + 'auth/listUsersRoles');
  }

  getRoles() {
    return this.http.get(this.baseUrl + 'auth/listRoles');
  }

  getRole(idRole: string): Observable<Role> {
    return this.http.get<Role>(this.baseUrl + 'auth/getRole/' + idRole);
  }

  getRolesFull() {
    return this.http.get(this.baseUrl + 'auth/listRolesFull');
  }

  updateUserRoles(user: User, roles: {}) {
    return this.http.post(
      this.baseUrl + 'auth/editUserRoles/' + user.userName,
      roles,
    );
  }

  getPermissionGroups(): Observable<PermissionGroup[]> {
    return this.http.get<PermissionGroup[]>(this.baseUrl + 'permissiongroups');
  }

  getAvailableTenantPermissions(): Observable<AvailablePermission[]> {
    return this.http.get<AvailablePermission[]>(this.baseUrl + 'permissiongroups/available-permissions');
  }

  createPermissionGroup(payload: { name: string; permissionNames: string[] }): Observable<PermissionGroup> {
    return this.http.post<PermissionGroup>(this.baseUrl + 'permissiongroups', payload);
  }

  updatePermissionGroup(id: string, payload: { name: string; permissionNames: string[] }): Observable<PermissionGroup> {
    return this.http.put<PermissionGroup>(this.baseUrl + 'permissiongroups/' + id, payload);
  }

  deletePermissionGroup(id: string) {
    return this.http.delete(this.baseUrl + 'permissiongroups/' + id);
  }

  getUserPermissionGroups(userId: string): Observable<{ userId: string; groupNames: string[] }> {
    return this.http.get<{ userId: string; groupNames: string[] }>(this.baseUrl + 'permissiongroups/user/' + userId);
  }

  updateUserPermissionGroups(userId: string, groupNames: string[]) {
    return this.http.post(this.baseUrl + 'permissiongroups/user/' + userId, { groupNames });
  }

  getEndpointPermissions(): Observable<EndpointPermission[]> {
    return this.http.get<EndpointPermission[]>(this.baseUrl + 'endpointpermissions');
  }

  updateEndpointPermission(id: string, payload: EndpointPermissionUpdate): Observable<EndpointPermission> {
    return this.http.put<EndpointPermission>(this.baseUrl + 'endpointpermissions/' + id, payload);
  }

  syncEndpointPermissions(): Observable<EndpointPermissionSyncResult> {
    return this.http.post<EndpointPermissionSyncResult>(this.baseUrl + 'endpointpermissions/sync', {});
  }

  getRemovedEndpoints(): Observable<RemovedEndpoint[]> {
    return this.http.get<RemovedEndpoint[]>(this.baseUrl + 'endpointpermissions/removed');
  }

  getAudits(
    limit?: number,
    offset?: number,
    searchString?: string,
  ): Observable<PaginationEnvelope<Audit>> {
    return this.http.get<PaginationEnvelope<Audit>>(
      this.baseUrl +
        'audits?' +
        'limit=' +
        (limit > 0 ? limit : 5) +
        '&' +
        'offset=' +
        (offset >= 0 ? offset : 0) +
        '&' +
        'searchString=' +
        (searchString == null ? '' : searchString),
    );
  }

  getAllAudits(
    searchString: string = '',
  ): Observable<PaginationEnvelope<Audit>> {
    const hugeLimit = 1000;
    return this.getAudits(hugeLimit, 0, searchString);
  }
}
